import type { ComponentType } from "react";
import { Role } from "@/utils";
import { DisposalRecoveryCodeEditPage } from "@/pages/DisposalRecoveryCodeEditPage/DisposalRecoveryCodeEditPage";
import { DisposalRecoveryCodesListPage } from "@/pages/DisposalRecoveryCodesListPage/DisposalRecoveryCodesListPage";
import { EwcCodeEditPage } from "@/pages/EwcCodeEditPage/EwcCodeEditPage";
import { EwcCodesListPage } from "@/pages/EwcCodesListPage/EwcCodesListPage";
import { GeographiesListPage } from "@/pages/GeographiesListPage/GeographiesListPage";
import { GeographyEditPage } from "@/pages/GeographyEditPage/GeographyEditPage";
import { MunicipalDegradableEditPage } from "@/pages/MunicipalDegradableEditPage/MunicipalDegradableEditPage";
import { MunicipalDegradablesListPage } from "@/pages/MunicipalDegradablesListPage/MunicipalDegradablesListPage";
import { PhysicalStateEditPage } from "@/pages/PhysicalStateEditPage/PhysicalStateEditPage";
import { PhysicalStatesListPage } from "@/pages/PhysicalStatesListPage/PhysicalStatesListPage";
import { PreTreatmentEditPage } from "@/pages/PreTreatmentEditPage/PreTreatmentEditPage";
import { PreTreatmentsListPage } from "@/pages/PreTreatmentsListPage/PreTreatmentsListPage";
import { SourceActivitiesListPage } from "@/pages/SourceActivitiesListPage/SourceActivitiesListPage";
import { SourceActivityEditPage } from "@/pages/SourceActivityEditPage/SourceActivityEditPage";

/** Shared path constants for regulatory reference admin modules (App routes + navigation). */
export const referenceAdminRoutePaths = {
    ewcCodes: {
        list: "/waste-codes",
        new: "/waste-codes/new",
        edit: "/waste-codes/:id",
    },
    disposalRecoveryCodes: {
        list: "/disposal-recovery-codes",
        new: "/disposal-recovery-codes/new",
        edit: "/disposal-recovery-codes/:id",
    },
    physicalStates: {
        list: "/physical-states",
        new: "/physical-states/new",
        edit: "/physical-states/:id",
    },
    preTreatments: {
        list: "/pre-treatments",
        new: "/pre-treatments/new",
        edit: "/pre-treatments/:id",
    },
    sourceActivities: {
        list: "/source-activities",
        new: "/source-activities/new",
        edit: "/source-activities/:id",
    },
    geography: {
        list: "/areas-districts",
        new: "/areas-districts/new",
        edit: "/areas-districts/:id",
    },
    municipalDegradables: {
        list: "/municipal-degradable-flags",
        edit: "/municipal-degradable-flags/:id",
    },
} as const;

export const referenceAdminRoles = [Role.Admin, Role.SystemAdmin];

export type ReferenceAdminRouteDef = {
    /** Stable key for React list rendering. */
    key: string;
    listPath: string;
    /** Present only when {@link hasCreateRoute} is true. */
    newPath?: string;
    editPath: string;
    listComponent: ComponentType;
    editComponent: ComponentType;
    roles: readonly string[];
    hasCreateRoute: boolean;
};

/**
 * Regulatory reference admin routes in App.tsx registration order:
 * for each module - create (if any), edit by id, then list.
 */
export const referenceAdminRoutes: readonly ReferenceAdminRouteDef[] = [
    {
        key: "waste-codes",
        listPath: referenceAdminRoutePaths.ewcCodes.list,
        newPath: referenceAdminRoutePaths.ewcCodes.new,
        editPath: referenceAdminRoutePaths.ewcCodes.edit,
        listComponent: EwcCodesListPage,
        editComponent: EwcCodeEditPage,
        roles: referenceAdminRoles,
        hasCreateRoute: true,
    },
    {
        key: "disposal-recovery-codes",
        listPath: referenceAdminRoutePaths.disposalRecoveryCodes.list,
        newPath: referenceAdminRoutePaths.disposalRecoveryCodes.new,
        editPath: referenceAdminRoutePaths.disposalRecoveryCodes.edit,
        listComponent: DisposalRecoveryCodesListPage,
        editComponent: DisposalRecoveryCodeEditPage,
        roles: referenceAdminRoles,
        hasCreateRoute: true,
    },
    {
        key: "physical-states",
        listPath: referenceAdminRoutePaths.physicalStates.list,
        newPath: referenceAdminRoutePaths.physicalStates.new,
        editPath: referenceAdminRoutePaths.physicalStates.edit,
        listComponent: PhysicalStatesListPage,
        editComponent: PhysicalStateEditPage,
        roles: referenceAdminRoles,
        hasCreateRoute: true,
    },
    {
        key: "pre-treatments",
        listPath: referenceAdminRoutePaths.preTreatments.list,
        newPath: referenceAdminRoutePaths.preTreatments.new,
        editPath: referenceAdminRoutePaths.preTreatments.edit,
        listComponent: PreTreatmentsListPage,
        editComponent: PreTreatmentEditPage,
        roles: referenceAdminRoles,
        hasCreateRoute: true,
    },
    {
        key: "source-activities",
        listPath: referenceAdminRoutePaths.sourceActivities.list,
        newPath: referenceAdminRoutePaths.sourceActivities.new,
        editPath: referenceAdminRoutePaths.sourceActivities.edit,
        listComponent: SourceActivitiesListPage,
        editComponent: SourceActivityEditPage,
        roles: referenceAdminRoles,
        hasCreateRoute: true,
    },
    {
        key: "areas-districts",
        listPath: referenceAdminRoutePaths.geography.list,
        newPath: referenceAdminRoutePaths.geography.new,
        editPath: referenceAdminRoutePaths.geography.edit,
        listComponent: GeographiesListPage,
        editComponent: GeographyEditPage,
        roles: referenceAdminRoles,
        hasCreateRoute: true,
    },
    {
        key: "municipal-degradable-flags",
        listPath: referenceAdminRoutePaths.municipalDegradables.list,
        editPath: referenceAdminRoutePaths.municipalDegradables.edit,
        listComponent: MunicipalDegradablesListPage,
        editComponent: MunicipalDegradableEditPage,
        roles: referenceAdminRoles,
        hasCreateRoute: false,
    },
];

/** List paths only, in the same order as {@link referenceAdminRoutes}. */
export const referenceAdminListPaths = referenceAdminRoutes.map((r) => r.listPath);

type ReferenceAdminModule = keyof typeof referenceAdminRoutePaths;

type ReferenceAdminModuleWithCreate = {
    [K in ReferenceAdminModule]: (typeof referenceAdminRoutePaths)[K] extends { new: string }
        ? K
        : never;
}[ReferenceAdminModule];

export function referenceAdminListPath(module: ReferenceAdminModule): string {
    return referenceAdminRoutePaths[module].list;
}

export function referenceAdminNewPath(module: ReferenceAdminModuleWithCreate): string {
    return referenceAdminRoutePaths[module].new;
}

export function referenceAdminEditPath(module: ReferenceAdminModule, id: string): string {
    return referenceAdminRoutePaths[module].edit.replace(":id", encodeURIComponent(id));
}
